import { NgIf, NgFor, NgClass, NgTemplateOutlet, SlicePipe } from "@angular/common";
import { Component, Input, Output, EventEmitter, ViewChild, ElementRef, HostListener, ViewChildren, QueryList, forwardRef, SimpleChanges, OnChanges, OnDestroy, ChangeDetectionStrategy, ChangeDetectorRef } from "@angular/core";
import { FormsModule, NG_VALUE_ACCESSOR } from "@angular/forms";
import { animate, style, transition, trigger, query, sequence, stagger, state } from "@angular/animations";
import { NiIconComponent } from "../ni-icon/ni-icon.component";   
import { TooltipDirective } from "../ni-tooltip/ni-tooltip.directive";
import { NiOptionComponent } from "../ni-option/ni-option.component";
import { NiChipComponent } from '../ni-chip/ni-chip.component';
import { NiSelectManagerService } from './ni-select-manager.service';
import { Subscription } from 'rxjs';
import { generateSecureRandomString } from '../../../../shared/utils/random-utils';

type SelectValue = string | number | Array<string | number>;

@Component({
  selector: "NiFormSelect",
  templateUrl: "./ni-select.component.html",
  styleUrls: ["./ni-select.component.css"],
  standalone: true,
  imports: [NgIf, NgFor, NgClass, NgTemplateOutlet, FormsModule, TooltipDirective, NiIconComponent, NiOptionComponent, NiChipComponent, SlicePipe],
  providers: [{ provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => NiSelectComponent), multi: true }],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('dropDownMenu', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(-10px)' }),
        // For large lists, skip stagger animation for better performance
        query('.menu-item', [
          style({ opacity: 0, transform: 'translateY(-20px)' })
        ], { optional: true }),
        sequence([
          animate('100ms ease-out', style({ opacity: 1, transform: 'translateY(0)' })),
          query('.menu-item', [
            stagger(10, [
              animate('50ms ease-out', style({ opacity: 1, transform: 'translateY(0)' }))
            ])
          ], { optional: true })
        ])
      ]),
      transition(':leave', [
        query('.menu-item', [
          stagger(5, [
            animate('50ms ease-in', style({ opacity: 0, transform: 'translateY(-10px)' }))
          ])
        ], { optional: true }),
        animate('80ms ease-in', style({ opacity: 0, transform: 'translateY(-10px)' }))
      ])
    ]),
  
    trigger('floatLabelAnimation', [
      transition(':enter', [
        style({ transform: 'translateY(0) scale(1)', opacity: 0 }),
        animate('200ms ease-out', style({ transform: 'translateY(-8px) scale(0.85)', opacity: 1 }))
      ]),
      transition(':leave', [
        animate('200ms ease-in', style({ transform: 'translateY(0) scale(1)', opacity: 0 }))
      ])
    ]),
  
    trigger('contentExpansion', [
      state('expanded', style({ height: '*', opacity: 1, visibility: 'visible' })),
      state('collapsed', style({ height: '0px', opacity: 0, visibility: 'hidden' })),
      transition('expanded <=> collapsed',
        animate('200ms cubic-bezier(.37,1.04,.68,.98)'))
    ]),
    trigger('skeletonFadeIn', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(-10px)' }),
        animate('200ms ease-out', style({ opacity: 1, transform: 'translateY(0)' })),
      ]),
      transition(':leave', [
        animate('150ms ease-in', style({ opacity: 0 })),
      ]),
    ]),
    trigger('inputFadeIn', [
      transition(':enter', [
        style({ opacity: 0, transform: 'scale(0.95)' }),
        animate('200ms ease-out', style({ opacity: 1, transform: 'scale(1)' })),
      ]),
    ]),
  ]
  
  
})

export class NiSelectComponent implements OnChanges, OnDestroy {
  // Uses cryptographically secure random values for component instance tracking
  private readonly componentId = generateSecureRandomString(9);
  private readonly closeAllSubscription: Subscription;
  
  // Autocomplete prevention - use cryptographically secure random values
  // Uses crypto.getRandomValues() to satisfy security scanner requirements
  private readonly autocompleteValue: string = 'nope-' + generateSecureRandomString(13);
  private readonly autocompleteSafeName: string = 'select-' + generateSecureRandomString(7);
  @Input() id: string = `select-${generateSecureRandomString(7)}`;
  @Input() label: string = "";
  @Input() selected: SelectValue = "";
  @Input() options: Array<any> = [];
  @Input() valueField: string | number = "";
  @Input() stateField: string | number = "";
  @Input() placeholder: string = "";
  @Input() disabled: boolean = false;
  @Input() readonly: boolean = false;
  @Input() required: boolean = false;
  @Input() hasError: boolean = false;
  @Input() errorMessage: string = "";
  @Input() multiple: boolean = false;
  @Input() isNestedToLeading: boolean = false;
  @Input() isNestedToTrailing: boolean = false;
  @Input() showFavoriteToggle: boolean = false;
  @Input() showBadge: boolean = false;
  @Input() showActionSection: boolean = false;
  @Input() actionSectionPosition: 'top' | 'bottom' = 'bottom';
  @Input() actionSectionAlignment: 'left' | 'center' | 'right' = 'right';
  @Input() isPopover : boolean = false
  @Input() actionButtons: Array<{label: string, icon?: string, action: string, disabled?: boolean, variant?: 'primary' | 'secondary' | 'danger' | 'success'}> = [];
  @Input() isLoading: boolean = false;
  @Input() clearable: boolean = false;
  /** When true, styles trailing " - Xmiles" in Figma success green (#2E7D32). */
  @Input() highlightDistanceMiles: boolean = false;

  @Output() onSelect: EventEmitter<any> = new EventEmitter<any>();
  @Output() onSearch: EventEmitter<string> = new EventEmitter<string>();
  @Output() selectedChange: EventEmitter<SelectValue> = new EventEmitter<SelectValue>();
  @Output() favoriteToggle: EventEmitter<{option: any, isFavorite: boolean}> = new EventEmitter<{option: any, isFavorite: boolean}>();
  @Output() actionButtonClick: EventEmitter<{action: string, event: Event}> = new EventEmitter<{action: string, event: Event}>();

  @ViewChild('containerRef') containerRef!: ElementRef;
  @ViewChild('optionsRef') optionsRef!: ElementRef;
  @ViewChildren('optionRefs') optionRefs!: QueryList<ElementRef>;

  showOptions: boolean = false;
  query: string = "";
  filteredOptions: Array<any> = [];
  selectedOptions: Array<any> = [];
  optionsPosition: 'above' | 'below' = 'below';
  optionsMaxHeight: number = 240;
  highlightedIndex: number = -1;

  // Memoized favorite options for performance
  private _favoriteOptions: Array<any> = [];
  private _nonFavoriteOptions: Array<any> = [];
  private _favoriteOptionsCacheKey: string = '';
  private readonly LARGE_LIST_THRESHOLD = 50; // Skip stagger animation for lists > 50 items

  private onChange: (value: SelectValue) => void = () => { };
  private onTouched: () => void = () => { };
  /** Value received via writeValue before options were available. */
  private pendingWriteValue: SelectValue | undefined = undefined;

  /**
   * Get the value field name with fallback to 'value'
   */
  private getValueField(): string | number {
    return this.valueField || 'value';
  }

  /**
   * Get the state field name with fallback to 'label'
   */
  private getStateField(): string | number {
    return this.stateField || 'label';
  }

  constructor(
    private readonly selectManager: NiSelectManagerService,
    private readonly cdr: ChangeDetectorRef
  ) {
    this.closeAllSubscription = this.selectManager.closeAll$.subscribe(exceptId => {
      if (exceptId !== this.componentId) {
        this.showOptions = false;
        this.cdr.markForCheck();
      }
    });
  }

  ngOnDestroy(): void {
    this.closeAllSubscription.unsubscribe();
  }

  writeValue(value: SelectValue): void {
    if (!this.options || this.options.length === 0) {
      // Options not loaded yet — apply when options arrive in ngOnChanges.
      this.pendingWriteValue = value;
      return;
    }
    this.pendingWriteValue = undefined;
    this.applyWriteValue(value);
  }

  private applyWriteValue(value: SelectValue): void {
    const valueField = this.getValueField();
    const stateField = this.getStateField();
    
    if (this.multiple) {
      // Coerce a single string/number into an array so edit prefills bind correctly
      const values: Array<string | number> =
        Array.isArray(value)
          ? value
          : value !== null && value !== undefined && value !== ''
            ? [value as string | number]
            : [];

      if (values.length > 0) {
        this.selected = this.options.filter(option => {
          const optValue = option[valueField];
          const optLabel = option[stateField];
          return values.some(v =>
            v === optValue ||
            v === optLabel ||
            String(v) === String(optValue) ||
            String(v) === String(optLabel)
          );
        }).map(option => option[stateField]);
        this.selectedOptions = this.options.filter(option => {
          const optValue = option[valueField];
          return values.some(v =>
            v === optValue ||
            String(v) === String(optValue)
          );
        });
      } else {
        this.selected = [];
        this.selectedOptions = [];
      }
    } else {
      // Use loose comparison to handle type mismatches (string vs number, etc.)
      const foundOption = this.options.find(option => {
        const optValue = option[valueField];
        const optLabel = option[stateField];
        return (
          optValue === value || 
          optLabel === value || 
          String(optValue) === String(value) || 
          String(optLabel) === String(value) ||
          (value != null && optValue != null && String(optValue).trim() === String(value).trim()) ||
          (value != null && optLabel != null && String(optLabel).trim() === String(value).trim())
        );
      });
      this.selected = foundOption?.[stateField] || "";
    }
    this.cdr.markForCheck();
  }

  registerOnChange(fn: (value: SelectValue) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['options']) {
      this.filteredOptions = this.options;
      // Invalidate favorite options cache when options change
      this._favoriteOptionsCacheKey = '';
      // Don't reset selected when options change - preserve current selection
      // Only reset if it's multiple and we're clearing or not initialized as array
      if (this.multiple && !(this.selected instanceof Array)) {
        this.selected = [];
        this.selectedOptions = [];
      }
      // Apply value that arrived before options were ready
      if (this.pendingWriteValue !== undefined && this.options?.length) {
        const pending = this.pendingWriteValue;
        this.pendingWriteValue = undefined;
        this.applyWriteValue(pending);
      } else if (!this.multiple && this.selected) {
        // For single select, try to preserve the current selection by re-matching
        const stateField = this.getStateField();
        const found = this.options.find(opt => opt[stateField] === this.selected);
        if (!found) {
          // Selection no longer exists in options, clear it
          this.selected = "";
        }
      }
      this.cdr.markForCheck();
    }
  }

  filterOptions() {
    const stateField = this.getStateField();
    this.filteredOptions = this.options.filter(option => {
      const fieldValue = option[stateField];
      return fieldValue && String(fieldValue).toLowerCase().includes(this.query.toLowerCase());
    });
    // Invalidate cache when filtering
    this._favoriteOptionsCacheKey = '';
    this.toggleOptions();
    this.onSearch.emit(this.query);
    this.cdr.markForCheck();
  }

  selectOption(option: any) {
    if (!option) {
      
      return;
    }
    
    
    
    if (this.multiple) {
      this.toggleMultiSelectOption(option);
    } else {
      this.selectSingleOption(option);
      this.focusContainer();
    }
  }

  selectSingleOption(option: any) {
    if (!option) {
      
      return;
    }
    
    const stateField = this.getStateField();
    const valueField = this.getValueField();
    
   
    
    // Set the selected label for display
    this.selected = option[stateField] || option.label || option.name || option.value || "";
        
    // Close the dropdown
    this.showOptions = false;
    
    // Emit the full option object
    this.onSelect.emit(option);
    
    // Emit the VALUE (not the label) so form gets the interpolated value for dynamic options
    let valueToEmit;

    if (option[valueField] !== undefined) {
      valueToEmit = option[valueField];
    } else if (option.value !== undefined) {
      valueToEmit = option.value;
    } else {
      valueToEmit = option[stateField];
    }

    
    
    // Emit to both selectedChange and onChange
    this.selectedChange.emit(valueToEmit);
    this.onChange(valueToEmit);
    this.onTouched();
    this.cdr.markForCheck();
  }

  toggleMultiSelectOption(option: any) {
    // Initialize selected as empty array if not already an array (for multiple mode)
    if (!(this.selected instanceof Array)) {
      this.selected = [];
      this.selectedOptions = [];
    }
    
    const valueField = this.getValueField();
    const stateField = this.getStateField();
    const index = this.selected.findIndex(selected => selected === option[stateField]);

    if (index === -1) {
      this.selected.push(option[stateField]);
      this.selectedOptions.push(option);
    } else {
      this.selected.splice(index, 1);
      this.selectedOptions.splice(index, 1);
    }

    this.query = "";
    this.onSelect.emit(this.selectedOptions);
    // Emit the VALUES (not the labels) so form gets the original template strings for dynamic options
    const selectedValues = this.selectedOptions.map(opt => opt[valueField]);
    this.selectedChange.emit(selectedValues);
    this.onChange(selectedValues);
    this.cdr.markForCheck();
  }

  preventFocus(event: FocusEvent): void {
    (event.target as HTMLElement).blur();
  }

  onClear() {
    this.selected = this.multiple ? [] : "";
    this.query = "";
    this.filteredOptions = this.options;
    this.selectedOptions = [];
    this._favoriteOptionsCacheKey = '';
    this.selectedChange.emit(this.selected);
    this.onChange(this.selected);
    this.cdr.markForCheck();
  }

  hasClearableSingleSelection(): boolean {
    if (this.multiple || !this.clearable) {
      return false;
    }
    const s = this.selected;
    if (s === null || s === undefined) {
      return false;
    }
    if (typeof s === 'string') {
      return s.trim() !== '';
    }
    return String(s).trim() !== '';
  }

  onClearButtonClick(event: MouseEvent): void {
    event.stopPropagation();
    event.preventDefault();
    this.showOptions = false;
    this.highlightedIndex = -1;
    this.onClear();
  }

  onRemoveItem(option: string | number) {
    if (this.selected instanceof Array) {
      const valueField = this.getValueField();
      const stateField = this.getStateField();
      this.selected = this.selected.filter(selected => selected !== option);
      this.selectedOptions = this.selectedOptions.filter(selected => selected[valueField] !== option && selected[stateField] !== option);
      this.selectedChange.emit(this.selected);
      this.onChange(this.selectedOptions.map(opt => opt[valueField]));
      this.cdr.markForCheck();
    }
  }

  toggleOptions() {
    if (this.query.length > 0) {
      this.showOptions = true;
    } else {
      this.showOptions = !this.showOptions;
    }
    
    if (this.showOptions) {
      this.selectManager.closeAllExcept(this.componentId);
      this.highlightedIndex = -1;
      // Invalidate favorite options cache when opening
      this._favoriteOptionsCacheKey = '';
      setTimeout(() => {
        this.determineOptionsPosition();
        // Focus the listbox when it opens to ensure keyboard events are handled
        if (this.optionsRef?.nativeElement) {
          this.optionsRef.nativeElement.focus();
        }
        this.cdr.markForCheck();
      }, 10);
    } else {
      this.highlightedIndex = -1;
    }
    this.cdr.markForCheck();
  }

  determineOptionsPosition(): void {
    if (!this.containerRef) return;

    const rect = this.containerRef.nativeElement.getBoundingClientRect();
    const optionsHeight = this.optionsRef?.nativeElement?.offsetHeight || 240;
    const headerOffset = this.isPopover ? 160 : 90;
    const safeMargin = 12;
    const spaceBelow = Math.max(0, window.innerHeight - rect.bottom - safeMargin);
    const spaceAbove = Math.max(0, rect.top - headerOffset - safeMargin);

    const enoughBelow = spaceBelow >= Math.min(optionsHeight, 220);
    if (enoughBelow || spaceBelow >= spaceAbove) {
      this.optionsPosition = 'below';
      this.optionsMaxHeight = Math.max(160, Math.min(320, Math.floor(spaceBelow - 4)));
    } else {
      this.optionsPosition = 'above';
      this.optionsMaxHeight = Math.max(160, Math.min(320, Math.floor(spaceAbove - 4)));
    }
  }

  handleKeyDown(event: KeyboardEvent) {
    switch (event.key) {
      case 'Enter':
        event.preventDefault();
        this.showOptions = !this.showOptions;
        if (this.showOptions) {
          this.highlightedIndex = -1;
          // Focus the listbox when opened with Enter
          setTimeout(() => {
            if (this.optionsRef?.nativeElement) {
              this.optionsRef.nativeElement.focus();
            }
          }, 10);
        }
        return;
      case 'ArrowUp':
      case 'ArrowDown':
        if (!this.showOptions) {
          // Only handle if dropdown is closed - open it and initialize
          event.preventDefault();
          this.showOptions = true;
          // Initialize highlighted index when opening with arrow keys
          this.highlightedIndex = event.key === 'ArrowDown' ? 0 : this.getAllOptions().length - 1;
          // Focus the listbox and let it handle subsequent navigation
          setTimeout(() => {
            if (this.optionsRef?.nativeElement) {
              this.optionsRef.nativeElement.focus();
            }
          }, 10);
        } else {
          // If dropdown is already open, ensure listbox has focus
          // The listbox will handle the event if it has focus
          event.preventDefault();
          if (this.optionsRef?.nativeElement) {
            this.optionsRef.nativeElement.focus();
            // Manually trigger the listbox handler since we prevented default
            this.handleListboxKeyDown(event);
          }
        }
        return;
      default:
        return;
    }
  }

  /**
   * Get all available options in display order (favorites first, then non-favorites, or all if favorites disabled)
   */
  getAllOptions(): any[] {
    if (this.showFavoriteToggle) {
      return [...this.favoriteOptions, ...this.nonFavoriteOptions];
    }
    return this.filteredOptions;
  }

  /**
   * Get the option at the specified index from all available options
   */
  getOptionAtIndex(index: number): any {
    const allOptions = this.getAllOptions();
    return allOptions[index] ?? null;
  }

  /**
   * Handle keyboard events at the listbox level (WAI-ARIA compliant)
   */
  handleListboxKeyDown(event: KeyboardEvent): void {
    if (!this.showOptions) {
      return;
    }

    const allOptions = this.getAllOptions();
    if (allOptions.length === 0) {
      return;
    }

    switch (event.key) {
      case 'ArrowDown': {
        event.preventDefault();
        event.stopPropagation();
        // Initialize to 0 if not set, otherwise increment
        if (this.highlightedIndex < 0) {
          this.highlightedIndex = 0;
        } else {
          this.highlightedIndex = (this.highlightedIndex + 1) % allOptions.length;
        }
        this.scrollToHighlightedOption();
        this.cdr.markForCheck();
        return;
      }
      case 'ArrowUp': {
        event.preventDefault();
        event.stopPropagation();
        // Initialize to last if not set, otherwise decrement
        if (this.highlightedIndex < 0) {
          this.highlightedIndex = allOptions.length - 1;
        } else {
          this.highlightedIndex = (this.highlightedIndex - 1 + allOptions.length) % allOptions.length;
        }
        this.scrollToHighlightedOption();
        this.cdr.markForCheck();
        return;
      }
      case 'Enter':
      case ' ': {
        event.preventDefault();
        event.stopPropagation();
        if (this.highlightedIndex >= 0 && this.highlightedIndex < allOptions.length) {
          const option = allOptions[this.highlightedIndex];
          this.selectOption(option);
        }
        return;
      }
      case 'Escape': {
        event.preventDefault();
        event.stopPropagation();
        this.showOptions = false;
        this.highlightedIndex = -1;
        setTimeout(() => this.focusContainer(), 0);
        this.cdr.markForCheck();
        return;
      }
      case 'Home': {
        event.preventDefault();
        event.stopPropagation();
        this.highlightedIndex = 0;
        this.scrollToHighlightedOption();
        this.cdr.markForCheck();
        return;
      }
      case 'End': {
        event.preventDefault();
        event.stopPropagation();
        this.highlightedIndex = allOptions.length - 1;
        this.scrollToHighlightedOption();
        this.cdr.markForCheck();
        return;
      }
    }
  }

  /**
   * Scroll the highlighted option into view
   */
  private scrollToHighlightedOption(): void {
    if (this.highlightedIndex < 0 || !this.optionsRef) {
      return;
    }

    const optionId = this.getOptionId(this.highlightedIndex);
    const element = this.optionsRef.nativeElement.querySelector(`#${optionId}`);
    if (element) {
      element.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }

  /**
   * Get the ID for an option element (for aria-activedescendant)
   */
  getOptionId(index: number): string {
    return `${this.id}-option-${index}`;
  }

  /**
   * Get the aria-activedescendant value for the listbox
   */
  getActiveDescendant(): string | null {
    if (this.highlightedIndex >= 0) {
      return this.getOptionId(this.highlightedIndex);
    }
    return null;
  }

  focusContainer() {
    setTimeout(() => this.containerRef?.nativeElement.focus(), 0);
  }

  getSelected(): Array<any> {
    return this.selected instanceof Array ? this.selected : [this.selected];
  }

  get value(): SelectValue {
    // Return the selected label for display in the input
    if (this.multiple) {
      return this.selected instanceof Array ? this.selected.join(', ') : this.selected;
    }
    return this.selected || "";
  }

  /** Figma success green used for distance miles suffix. */
  readonly distanceMilesColor = '#2E7D32';

  private readonly distanceMilesPattern =
    /^(.*?)(\s*-\s*\d+(?:\.\d+)?\s*miles?(?:\s*\(Recommended\))?)\s*$/i;

  splitDistanceLabel(label: string | null | undefined): { name: string; miles: string } | null {
    const text = String(label ?? '');
    if (!this.highlightDistanceMiles || !text) {
      return null;
    }
    const match = text.match(this.distanceMilesPattern);
    if (!match) {
      return null;
    }
    return { name: match[1], miles: match[2] };
  }

  get displayDistanceParts(): { name: string; miles: string } | null {
    return this.splitDistanceLabel(String(this.value ?? ''));
  }

  @HostListener('document:click', ['$event'])
  onClickOutside(event: Event) {
    const target = event.target as Node;
    // Check if click is outside both container and options dropdown
    const isClickInsideContainer = this.containerRef?.nativeElement.contains(target);
    const isClickInsideOptions = this.optionsRef?.nativeElement.contains(target);
    
    // Only close if click is completely outside both elements
    // The stopPropagation() calls in option click handlers will prevent this from firing
    // when clicking on options
    if (!isClickInsideContainer && !isClickInsideOptions) {
      this.showOptions = false;
      this.highlightedIndex = -1;
      this.cdr.markForCheck();
    }
  }

  onFavoriteToggle(event: {option: any, isFavorite: boolean}): void {
    // Update the option's isFavorite property
    event.option.isFavorite = event.isFavorite;
    
    // Invalidate cache when favorite status changes
    this._favoriteOptionsCacheKey = '';
    
    // Emit the event to parent component
    this.favoriteToggle.emit(event);
    this.cdr.markForCheck();
  }

  onActionButtonClick(action: string, event: Event): void {
    event.stopPropagation();
    this.actionButtonClick.emit({ action, event });
  }

  get favoriteOptions(): any[] {
    // Create cache key based on filteredOptions length and favorite state
    const cacheKey = `${this.filteredOptions.length}-${this.filteredOptions.filter(o => o.isFavorite).length}`;
    
    // Return cached value if available and still valid
    if (this._favoriteOptionsCacheKey === cacheKey && this._favoriteOptions.length > 0) {
      return this._favoriteOptions;
    }
    
    // Recompute and cache
    this._favoriteOptions = this.filteredOptions.filter(option => option.isFavorite);
    this._favoriteOptionsCacheKey = cacheKey;
    return this._favoriteOptions;
  }

  get nonFavoriteOptions(): any[] {
    // Create cache key based on filteredOptions length and favorite state
    const cacheKey = `${this.filteredOptions.length}-${this.filteredOptions.filter(o => !o.isFavorite).length}`;
    
    // Return cached value if available and still valid
    if (this._favoriteOptionsCacheKey === cacheKey && this._nonFavoriteOptions.length > 0) {
      return this._nonFavoriteOptions;
    }
    
    // Recompute and cache
    this._nonFavoriteOptions = this.filteredOptions.filter(option => !option.isFavorite);
    this._favoriteOptionsCacheKey = cacheKey;
    return this._nonFavoriteOptions;
  }

  /**
   * TrackBy function for option lists to improve rendering performance
   */
  trackByOption = (index: number, option: any): any => {
    const stateField = this.getStateField();
    const valueField = this.getValueField();
    // Use a combination of value and state field for unique identification
    return option[valueField] ?? option[stateField] ?? option.value ?? option.label ?? index;
  }

  /**
   * TrackBy function for selected options chips
   */
  trackBySelectedOption = (index: number, option: any): any => {
    const stateField = this.getStateField();
    const valueField = this.getValueField();
    return option[valueField] ?? option[stateField] ?? option.value ?? option.label ?? index;
  }

  /**
   * Check if we should use simplified animations (skip stagger for large lists)
   */
  shouldUseSimplifiedAnimation(): boolean {
    return this.filteredOptions.length > this.LARGE_LIST_THRESHOLD;
  }

  /**
   * TrackBy function for action buttons
   */
  trackByActionButton = (index: number, button: any): any => {
    return button.action ?? button.label ?? index;
  }

  getAutocompleteValue(): string {
    // Use a random value that browsers won't recognize to prevent autocomplete
    return this.autocompleteValue;
  }

  getAutocompleteSafeName(): string {
    // Modify the name attribute to prevent browser autocomplete
    // Browsers use name/id attributes to identify fields for autocomplete
    return this.autocompleteSafeName;
  }
}